<!--
  One reference letter form, at a given height.

  This is the only place that knows five things, and it is one component so that they cannot
  drift apart across the five surfaces that show a form:

  - **The tile.** The PNGs are opaque black marks on a *transparent* ground. Dropped straight onto
    this UI they would be black on near-black — invisible, and invisibly so. Hence the paper
    background, which is a property of the artwork rather than of any one screen.
  - **The aspect ratio**, from `ALPHABET_SOURCE` rather than a literal, so a regenerated table at a
    different size does not silently squash every glyph.
  - **The mirror and the quarter turn**, as CSS transforms of the one stored reference form. No
    mirrored PNG is stored, no turned PNG is stored, and mirrored forms are never separate
    classes. Both are decided by `glyphOrientation`, never here.
  - **The footprint of a turned form.** A quarter turn swaps a glyph's width and height, and a
    CSS transform does not move layout, so a turned form is wrapped in a slot of the swapped
    size. Without it a vertical record's forms would overlap their neighbours and jag the row —
    the same reason the empty tile exists at full size.
  - **The refusal.** A class with no form gets an explicit empty tile of the *same* size, never an
    `<img>` and never a substitute. `illegible` and `unknown` have no form and never will; a
    palette that quietly filled that gap would be teaching a letter that does not exist.
-->
<script lang="ts">
  import { glyphUri, glyphWidthFor } from '../lib/glyphs';

  interface Props {
    /** Vocabulary class id — `TokenOut.letter_class` or `VocabEntryOut.id`. */
    letterClass: number;
    /** Flip horizontally. Decided by `glyphOrientation(direction)`, never here. */
    mirrored?: boolean;
    /** Quarter turn for vertical writing. Decided by `glyphOrientation(direction)`, never here. */
    turned?: boolean;
    /** Rendered height in px; the width follows the stored aspect ratio. */
    height?: number;
    /** The class's display form, for the alt text ('s¹', 'ṭ'). */
    label: string;
  }

  const { letterClass, mirrored = false, turned = false, height = 28, label }: Props = $props();

  const uri = $derived(glyphUri(letterClass));
  const width = $derived(glyphWidthFor(height));
  // What the form occupies once it is drawn: a quarter turn trades the two.
  const boxWidth = $derived(turned ? height : width);
  const boxHeight = $derived(turned ? width : height);
</script>

{#if uri}
  <!-- `display: contents` while upright, so the untouched case lays out exactly as it did
       before the turn existed and only a turned form pays for a wrapper. -->
  <span
    class="glyph-slot"
    class:turned
    style={turned ? `width: ${boxWidth}px; height: ${boxHeight}px;` : ''}
  >
    <img
      class="glyph-form"
      class:mirror={mirrored}
      class:turned
      src={uri}
      alt={`Hismaic reference form for ${label}`}
      {width}
      {height}
    />
  </span>
{:else}
  <span
    class="glyph-form no-form"
    style="width: {boxWidth}px; height: {boxHeight}px;"
    title={`No reference form for ${label}`}
    aria-hidden="true"
  ></span>
{/if}

<style>
  .glyph-form {
    display: block;
    flex: none;
    border-radius: 2px;
  }

  img.glyph-form {
    background: var(--glyph-paper);
  }

  /* This tool *treats* the stored reference form as the upright, right-to-left one — a
     convention it adopted, not a fact it established; which physical form the shipped artifact
     actually is remains open (guidelines §12 F1). Writing that runs against the reference
     mirrors; writing that runs vertically is a quarter turn off it; bottom-to-top is both. The
     turn is `-90deg` so the reference's leftward travel comes to point downwards. Order
     matters: the mirror is in the letter's own frame, the turn is the line's. */
  .mirror {
    transform: scaleX(-1);
  }

  img.turned {
    transform: rotate(-90deg);
  }

  img.turned.mirror {
    transform: rotate(-90deg) scaleX(-1);
  }

  .glyph-slot {
    display: contents;
  }

  .glyph-slot.turned {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
  }

  /* Same footprint as a form, so a lacuna chip sits level with its neighbours instead of
     jagging the row — the gap is stated, not hidden. */
  .no-form {
    border: 1px dashed var(--line-strong);
    background: transparent;
  }
</style>
